// Diagnostics for the "blank page after navigating back and forth" failure in
// the Flutter WebView embed.
//
// The app can end up showing nothing at all through several different paths
// (stalled bootstrap, auth never attempted, hash route with no topic), and all
// of them look identical on screen. These logs make the path taken observable
// from the WebView console.
//
// Every line is tagged with a per-page-instance id so that overlapping page
// instances (the old page is often still alive while the new one boots) can be
// told apart in a single log stream.
//
// All output is gated on LOGGING_ENABLED, so this is silent in production.

import { LOGGING_ENABLED } from '../config.js';

const BOOT_AT = Date.now();
const PAGE_ID = BOOT_AT.toString(36).slice(-6);

function elapsed() {
  return `+${Date.now() - BOOT_AT}ms`;
}

// The WebView console bridge stringifies each console argument instead of
// expanding it, turning an object into a useless "[object Object]". Any detail
// object therefore has to be serialized into the message string itself. Use for
// every console call that needs to be readable from the native host.
//
// `undefined` property values are kept visible because an unset field is often
// the answer, and anything that cannot be serialized degrades to a note rather
// than throwing: a diagnostic must never be the thing that breaks the page.
export function formatLogData(data) {
  if (data === undefined) {
    return '';
  }
  if (typeof data !== 'object' || data === null) {
    return String(data);
  }
  const seen = new WeakSet();
  try {
    return JSON.stringify(data, (key, value) => {
      if (value === undefined) {
        return '<undefined>';
      }
      if (value instanceof Error) {
        return value.message || String(value);
      }
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '<circular>';
        }
        seen.add(value);
      }
      return value;
    });
  } catch (err) {
    return `<unserializable: ${err instanceof Error ? err.message : String(err)}>`;
  }
}

function suffix(data) {
  const text = formatLogData(data);
  return text ? ` ${text}` : '';
}

// True when boot diagnostics will produce output. Check this before doing any
// work that exists only to build a log argument.
export function bootLogEnabled() {
  return LOGGING_ENABLED;
}

export function bootLog(tag, data) {
  if (!LOGGING_ENABLED) {
    return;
  }
  console.log(`[boot ${PAGE_ID} ${elapsed()}] ${tag}${suffix(data)}`);
}

export function bootWarn(tag, data) {
  if (!LOGGING_ENABLED) {
    return;
  }
  console.warn(`[boot ${PAGE_ID} ${elapsed()}] ${tag}${suffix(data)}`);
}

export function getPageId() {
  return PAGE_ID;
}

// Snapshot of everything that decides whether the app can authenticate.
// Tokens themselves are never logged, only their presence and length.
export function credentialSnapshot() {
  let storedToken = null;
  let storageError = null;
  try {
    storedToken = JSON.parse(localStorage.getItem('auth-token'));
  } catch (err) {
    storageError = err instanceof Error ? err.message : String(err);
  }
  let keepLoggedIn = null;
  try {
    keepLoggedIn = localStorage.getItem('keep-logged-in');
  } catch (err) {
    storageError = storageError || (err instanceof Error ? err.message : String(err));
  }

  return {
    url: globalThis.location.href.replace(/token=[^&]*/g, 'token=<redacted>'),
    hash: globalThis.location.hash.replace(/token=[^&]*/g, 'token=<redacted>'),
    hasStoredToken: !!storedToken?.token,
    storedTokenExpires: storedToken?.expires || null,
    storedTokenExpired: storedToken?.expires ? new Date(storedToken.expires).getTime() <= Date.now() : null,
    keepLoggedIn,
    hasBridgeJwt: !!(globalThis.__ANGKAS_JWT__ || '').trim(),
    hasFlutterBridge: typeof globalThis.FlutterBridge?.postMessage === 'function',
    storageError,
  };
}

// Installs page-lifecycle and global-failure listeners. Idempotent, and a no-op
// when logging is disabled so no listeners are attached at all.
export function initBootDiagnostics() {
  if (typeof globalThis === 'undefined' || globalThis.__bootDiagnosticsInstalled) {
    return;
  }
  if (!LOGGING_ENABLED) {
    return;
  }
  globalThis.__bootDiagnosticsInstalled = true;

  bootLog('page:load', credentialSnapshot());

  // persisted === true means the WebView restored this page from the
  // back/forward cache: React state and timers survive but the WebSocket and
  // any in-flight promises from the previous visit do not.
  globalThis.addEventListener('pageshow', event => {
    bootLog('page:pageshow', {bfcacheRestore: event.persisted, ...credentialSnapshot()});
  });

  globalThis.addEventListener('pagehide', event => {
    // A page that is only "hidden" (persisted) keeps its IndexedDB connection
    // open, which is what blocks the next page instance from deleting it.
    bootLog('page:pagehide', {enteringBfcache: event.persisted});
  });

  document.addEventListener('visibilitychange', () => {
    bootLog('page:visibilitychange', {hidden: document.hidden});
  });

  globalThis.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    bootWarn('page:unhandledrejection', {
      name: reason?.name,
      message: reason?.message || String(reason),
    });
  });

  globalThis.addEventListener('error', event => {
    bootWarn('page:error', {
      message: event.message,
      source: `${event.filename}:${event.lineno}`,
    });
  });
}
